import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

export const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
] as const;

/**
 * The effective option list for a property: its explicit `options`, or
 * `BOOLEAN_OPTIONS` when the field is `boolean` and has no explicit options,
 * or an empty list.
 */
export function getEffectiveOptions(
  propertyType: PropertyTypeSchema | undefined
): ReadonlyArray<{ value: string; label: string }> {
  if (
    propertyType?.field_type_name === 'boolean' &&
    !propertyType.options?.length
  ) {
    return BOOLEAN_OPTIONS;
  }
  return propertyType?.options ?? [];
}

/**
 * Resolve a stored option value to its display label, falling back to the
 * raw value when no option matches.
 *
 * Compare as strings: select inputs stringify option values (String(opt.value)
 * as the option id and in onSelectionChange), so a runtime-numeric schema
 * option value would never strictly equal the string the input hands back —
 * and a missed match falls through to rendering the raw option key.
 */
export function resolveOptionLabel(
  propertyType: PropertyTypeSchema | undefined,
  value: string
): string {
  const option = getEffectiveOptions(propertyType).find(
    opt => String(opt.value) === String(value)
  );
  return option?.label ?? value;
}
